import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../lib/language'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

export default function AboutPage() {
  const { t, language } = useLanguage()
  const isEs = language === 'es'
  const [scrolled, setScrolled] = useState(false)
  const [showTop, setShowTop] = useState(false)

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60)
      setShowTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  function scrollToTop() { window.scrollTo({ top: 0, behavior: 'smooth' }) }

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }

    .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
    :focus-visible { outline:2px solid #4D4E8D; outline-offset:3px; border-radius:6px; }

    .lca-hero { padding:64px 44px 48px; text-align:center; background:#E9EAEC; font-family:'Plus Jakarta Sans',sans-serif; }
    .lca-hero-eyebrow { font-size:12px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; color:#8A8F94; margin-bottom:16px; }
    .lca-hero-rule { width:44px; height:3px; background:#4D4E8D; border-radius:2px; margin:0 auto 22px; }
    .lca-hero h1 { font-family:'Plus Jakarta Sans',sans-serif; font-size:clamp(30px,3.6vw,44px); font-weight:800; color:#151715; margin-bottom:14px; line-height:1.14; letter-spacing:-.025em; }
    .lca-hero h1 span { color:#4D4E8D; }
    .lca-hero-sub { font-size:16px; font-weight:500; color:#5C6065; max-width:520px; margin:0 auto; line-height:1.7; }

    .lca-about { padding:64px 44px; max-width:1100px; margin:0 auto; font-family:'Plus Jakarta Sans',sans-serif; }
    @media(max-width:640px){ .lca-about { padding:44px 20px; } }

    .lca-about-grid { display:grid; grid-template-columns:1fr 1fr; gap:56px; align-items:start; margin-bottom:72px; }
    @media(max-width:860px){ .lca-about-grid { grid-template-columns:1fr; gap:36px; } }

    .lca-photo-wrap { display:flex; flex-direction:column; align-items:center; }
    .lca-photo { width:100%; max-width:560px; border-radius:18px; overflow:hidden; border:1px solid #E2E4E7; box-shadow:0 18px 44px rgba(21,23,21,0.12); margin-bottom:18px; background:#fff; }
    .lca-photo img { width:100%; height:auto; display:block; object-fit:cover; }
    .lca-founder-name { font-size:22px; font-weight:700; color:#151715; margin-bottom:3px; text-align:center; letter-spacing:-.01em; }
    .lca-founder-title { font-size:13px; font-weight:600; color:#4D4E8D; letter-spacing:.04em; text-align:center; }

    .lca-about-text { padding-top:6px; }
    .lca-quote { font-size:20px; font-weight:600; color:#151715; line-height:1.5; margin-bottom:26px; padding-left:20px; border-left:3px solid #4D4E8D; letter-spacing:-.01em; }
    .lca-about-body { font-size:14.5px; font-weight:500; color:#5C6065; line-height:1.85; margin-bottom:18px; }

    .lca-values { margin-bottom:72px; }
    .lca-values-title { font-size:clamp(26px,3vw,32px); font-weight:800; color:#151715; text-align:center; margin-bottom:42px; letter-spacing:-.02em; }
    .lca-values-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:20px; }
    @media(max-width:900px){ .lca-values-grid { grid-template-columns:repeat(2,1fr); } }
    @media(max-width:480px){ .lca-values-grid { grid-template-columns:1fr; } }
    .lca-value-card { text-align:center; padding:30px 20px; background:#fff; border:1px solid #E2E4E7; border-radius:18px; box-shadow:0 8px 22px rgba(21,23,21,0.05); transition:transform .2s, box-shadow .2s; }
    .lca-value-card:hover { transform:translateY(-4px); box-shadow:0 16px 36px rgba(21,23,21,0.10); }
    .lca-value-icon { display:flex; align-items:center; justify-content:center; margin:0 auto 18px; width:56px; height:56px; border-radius:15px; background:#EEF0F8; }
    .lca-value-icon svg { width:30px; height:30px; fill:#4D4E8D; }
    .lca-value-name { font-size:16px; font-weight:700; color:#151715; margin-bottom:10px; }
    .lca-value-desc { font-size:12.5px; font-weight:500; color:#5C6065; line-height:1.7; }

    .lca-vision { margin-bottom:8px; text-align:center; padding:48px 40px; background:#fff; border:1px solid #E2E4E7; border-radius:20px; box-shadow:0 10px 28px rgba(21,23,21,0.06); }
    .lca-vision-title { font-size:clamp(26px,3vw,32px); font-weight:800; color:#151715; margin-bottom:6px; line-height:1.2; letter-spacing:-.02em; }
    .lca-vision-title span { color:#4D4E8D; }
    .lca-vision-rule { width:80px; height:3px; background:#4D4E8D; margin:0 auto 22px; border-radius:2px; }
    .lca-vision-desc { font-size:15px; font-weight:500; color:#5C6065; max-width:680px; margin:0 auto; line-height:1.85; }

    .lca-cta { padding:60px 44px; text-align:center; }
    .lca-cta h2 { font-size:clamp(24px,2.8vw,30px); font-weight:800; color:#151715; margin-bottom:10px; letter-spacing:-.02em; }
    .lca-cta p { font-size:14px; font-weight:500; color:#5C6065; margin-bottom:24px; line-height:1.7; }
    .lca-cta-btn { background:#4D4E8D; color:#fff; padding:14px 32px; border-radius:12px; font-size:14px; font-weight:700; border:none; cursor:pointer; box-shadow:0 10px 22px rgba(77,78,141,0.22); text-decoration:none; display:inline-block; transition:transform .15s, background .2s; }
    .lca-cta-btn:hover { transform:translateY(-2px); background:#41427a; }
    .lca-cta-btn:focus-visible { outline:2px solid #4D4E8D; outline-offset:3px; }
    @media(max-width:640px){ .lca-cta { padding:44px 20px; } .lca-vision { padding:34px 22px; } }

    .lca-back-top-wrap { background:transparent; display:flex; justify-content:center; padding:0; }
    .lca-back-top { position:fixed; bottom:24px; right:20px; width:46px; height:46px; border-radius:50%; background:#4D4E8D; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:18px; color:#fff; font-weight:700; box-shadow:0 8px 20px rgba(77,78,141,0.35); transition:all .3s; opacity:0; pointer-events:none; z-index:150; }
    .lca-back-top.visible { opacity:1; pointer-events:auto; }
    .lca-back-top:hover { transform:translateY(-3px); }
  `

  const PrecisionIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
    </svg>
  )
  const TransparencyIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
    </svg>
  )
  const RelationshipsIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
    </svg>
  )
  const IntegrityIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
    </svg>
  )

  const coreValues = [
    { icon: <PrecisionIcon />, name_en: 'Precision', name_es: 'Precisión', desc_en: 'Every document is reviewed with meticulous attention to ensure accuracy and compliance.', desc_es: 'Cada documento es revisado con atención meticulosa para garantizar precisión y cumplimiento.' },
    { icon: <TransparencyIcon />, name_en: 'Transparency', name_es: 'Transparencia', desc_en: 'Clear communication and honest pricing with no hidden fees or surprises.', desc_es: 'Comunicación clara y precios honestos sin cargos ocultos ni sorpresas.' },
    { icon: <RelationshipsIcon />, name_en: 'Relationships', name_es: 'Relaciones', desc_en: 'We build lasting partnerships with clients, agents, and industry professionals.', desc_es: 'Construimos asociaciones duraderas con clientes, agentes y profesionales de la industria.' },
    { icon: <IntegrityIcon />, name_en: 'Integrity', name_es: 'Integridad', desc_en: 'Background-checked, insured agents you can trust with your most important transactions.', desc_es: 'Agentes con verificación de antecedentes y asegurados en quienes puedes confiar.' },
  ]

  return (
    <div style={{ background: '#E9EAEC', minHeight: '100vh', color: '#151715' }}>
      <style>{styles}</style>

      {/* NAV */}
      <SiteHeader />

      <main id="nsv-main" tabIndex={-1}>
        {/* HERO */}
        <section className="lca-hero" aria-labelledby="about-heading">
          <div className="lca-hero-eyebrow" aria-hidden="true">{isEs ? 'CONÓCENOS MÁS' : 'LEARN MORE ABOUT US'}</div>
          <div className="lca-hero-rule" aria-hidden="true" />
          <h1 id="about-heading">
            {isEs ? 'Construido sobre' : 'Built on'}{' '}
            <span>{isEs ? 'Experiencia' : 'Experience'}</span><br />
            {isEs ? 'Impulsado por Servicio' : 'Driven by Service'}
          </h1>
          <p className="lca-hero-sub">
            {isEs
              ? <>Desde 2019, conectando personas y empresas con<br />Agentes Notariales de Firma Certificados en todo el país.</>
              : <>Since 2019, connecting people and businesses with<br />Certified Notary Signing Agents across the nation.</>}
          </p>
        </section>

        <div className="lca-about">
          {/* PHOTO + TEXT */}
          <div className="lca-about-grid">
            <div className="lca-photo-wrap">
              <div className="lca-photo">
                <img src="/lca_about.jpg" alt="Nancy Phillips, Founder and CEO of Notary Signing Vault, LLC, seated at her desk in a professional office setting." onError={e => { e.target.style.display = 'none' }} />
              </div>
              <div className="lca-founder-name">Nancy Phillips</div>
              <div className="lca-founder-title">Founder &amp; CEO</div>
            </div>
            <div className="lca-about-text">
              <blockquote className="lca-quote">
                {isEs
                  ? '"Construido sobre la experiencia e impulsado por el servicio, Notary Signing Vault, LLC es su socio de confianza para firmas y notarizaciones en todo el país."'
                  : '"Built on experience and driven by service, Notary Signing Vault, LLC is your trusted partner for nationwide signings and notarizations."'}
              </blockquote>
              <p className="lca-about-body">
                {isEs
                  ? 'Notary Signing Vault, LLC fue fundada en 2019 con una misión simple: proporcionar servicios confiables y profesionales de firma de préstamos, notaría móvil y notarización remota en línea en los que compañías de título, prestamistas e individuos puedan depender a nivel nacional.'
                  : 'Notary Signing Vault, LLC was founded in 2019 with a simple mission: to provide reliable, professional loan signing, mobile notary, and remote online notarization services that title companies, lenders, and individuals can depend on nationwide. Built on the founder\'s experience as a notary since 2008 and as a seasoned notary signing agent with extensive hands-on expertise, the company brings real-world industry knowledge to every transaction.'}
              </p>
              <p className="lca-about-body">
                {isEs
                  ? 'A través de nuestro DBA, Nationwide Notary Professionals Network, hemos construido una red creciente de agentes notariales de firma verificados y experimentados en los 50 estados.'
                  : 'Through our DBA, Nationwide Notary Professionals Network, we have built a growing network of vetted and experienced notary signing agents across all 50 states. Every agent in our network is background-checked, insured, and committed to delivering accurate, timely signings.'}
              </p>
              <p className="lca-about-body">
                {isEs
                  ? 'Entendemos la importancia de cada transacción. Ya sea el cierre de compra de un comprador de vivienda por primera vez, una transacción comercial compleja o una simple notarización de poder notarial, abordamos cada asignación con el mismo nivel de profesionalismo, precisión y cuidado.\n\nSobre todo, le agradecemos por confiar en nosotros con sus transacciones y firmas.'
                  : 'We understand the importance of every transaction. Whether it\'s a first-time homebuyer\'s purchase closing, a complex commercial transaction, or a simple power of attorney notarization, we approach every assignment with the same level of professionalism, precision, and care.\n\nAbove all, we thank you for trusting us with your transactions and signings. It\'s a responsibility we value deeply, and we are honored to support you with the highest level of service.'}
              </p>
            </div>
          </div>

          {/* CORE VALUES */}
          <section aria-labelledby="values-heading" className="lca-values">
            <h2 id="values-heading" className="lca-values-title">{isEs ? 'Nuestros Valores Fundamentales' : 'Our Core Values'}</h2>
            <div className="lca-values-grid">
              {coreValues.map((v, i) => (
                <div key={i} className="lca-value-card">
                  <div className="lca-value-icon">{v.icon}</div>
                  <div className="lca-value-name">{isEs ? v.name_es : v.name_en}</div>
                  <div className="lca-value-desc">{isEs ? v.desc_es : v.desc_en}</div>
                </div>
              ))}
            </div>
          </section>

          {/* NATIONWIDE VISION */}
          <section aria-labelledby="vision-heading" className="lca-vision">
            <h2 id="vision-heading" className="lca-vision-title">
              {isEs ? <>Nuestra <span>Visión</span> Nacional</> : <>Our Nationwide <span>Vision</span></>}
            </h2>
            <div className="lca-vision-rule" aria-hidden="true" />
            <p className="lca-vision-desc">
              {isEs
                ? 'Estamos creciendo nuestra red cada día para asegurarnos de que sin importar dónde en el país se necesite una firma, haya un agente calificado y de confianza cerca y listo.'
                : 'We\'re growing our network every day to ensure that no matter where in the country a signing is needed, a qualified, trusted agent is nearby and ready. Our vision is to be the go-to resource for loan signings and notary services nationwide.'}
            </p>
          </section>
        </div>

        {/* CTA */}
        <div className="lca-cta">
          <h2>{isEs ? '¿Listo para Unirse a Nosotros?' : 'Ready to Join Us?'}</h2>
          <p>
            {isEs
              ? 'Contáctanos hoy y descubre por qué cientos de clientes confían en Notary Signing Vault, LLC.'
              : 'Contact us today and discover why hundreds of clients trust Notary Signing Vault, LLC.'}
          </p>
          <Link to="/contact" className="lca-cta-btn" aria-label={isEs ? 'Contáctanos hoy' : 'Contact us today'}>
            {isEs ? 'Contáctanos Hoy' : 'Contact Us Today'}
          </Link>
        </div>
      </main>

      {/* BACK TO TOP */}
      <div className="lca-back-top-wrap">
        <button className={`lca-back-top${showTop ? ' visible' : ''}`} onClick={scrollToTop} aria-label="Scroll back to top" aria-hidden={!showTop} tabIndex={showTop ? 0 : -1}>↑</button>
      </div>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  )
}
